import { Controller } from "@hotwired/stimulus"

import { hostPage } from "@/lib/embed_host"
import { frameFetch } from "@/lib/frame_fetch"

import type { AccessAnswer } from "./embed_access_controller"
import type PreviewController from "./preview_controller"

// What the card offers whoever is signed in to the frame (#2041). The card is
// one cached copy for everyone, so it follows each private access answer. An
// anonymous Join only opens the code form: only the emailed code can join, so
// a stray click never signs a stranger up.
export default class extends Controller {
  static targets = ["gate", "player", "account", "email", "signIn"]
  static outlets = ["preview"]
  static values = {
    membershipUrl: String,
    sessionUrl: String,
    offerUrl: String,
    paid: Boolean,
  }

  declare readonly gateTargets: HTMLElement[]
  declare readonly playerTargets: HTMLElement[]
  declare readonly accountTargets: HTMLElement[]
  declare readonly emailTargets: HTMLElement[]
  declare readonly signInTargets: HTMLElement[]
  declare readonly previewOutlets: PreviewController[]
  declare membershipUrlValue: string
  declare sessionUrlValue: string
  declare offerUrlValue: string
  declare paidValue: boolean

  #answer: AccessAnswer | null = null
  #closeOnAnswer = false
  #waitingForAnswer: ((answer: AccessAnswer | null) => void) | null = null
  #hostPage: string | null = null
  // Several padlocks can ask to join at once; one request is enough.
  #writing = false

  // Where a join came from (#2243): read once, as the card first loads.
  connect() {
    this.#hostPage = hostPage()
  }

  // data-action="embed-access:changed->embed-account#answered"
  answered(event: CustomEvent<AccessAnswer>) {
    const answer = event.detail
    this.#answer = answer
    const playable = answer.state === "playable"
    const signedIn = answer.authentication === "signed_in"

    // A paid card's preview must not keep playing behind the full player.
    if (playable) this.previewOutlets.forEach((preview) => preview.stop())
    this.element.setAttribute("data-embed-state", answer.state)
    this.gateTargets.forEach((el) => (el.hidden = playable))
    this.playerTargets.forEach((el) => (el.hidden = !playable))
    this.accountTargets.forEach((el) => (el.hidden = !signedIn))
    this.emailTargets.forEach(
      (el) => (el.textContent = answer.listener?.email ?? ""),
    )
    this.signInTargets.forEach(
      (el) =>
        (el.hidden = signedIn || answer.state !== "authentication_required"),
    )

    if (this.#closeOnAnswer) {
      this.#closeOnAnswer = false
      this.dispatch("done")
    }
    this.#waitingForAnswer?.(answer)
    this.#waitingForAnswer = null
  }

  // data-action="embed-access:failed->embed-account#unreachable"
  unreachable() {
    this.#waitingForAnswer?.(null)
    this.#waitingForAnswer = null
  }

  // Join stays the link to Indee until the frame has an answer: a listener on
  // a slow network or with the access answer refused still has a way in.
  join(event: Event) {
    if (this.joinInFrame()) event.preventDefault()
  }

  // data-action="embed-player:locked->embed-account#unlock"
  // A padlock row or the transport's padlock (#2242) does what the card's
  // offer does: a free card joins in the frame once it knows who is
  // listening, and paying, or a card still waiting, goes to Indee.
  unlock() {
    if (this.paidValue || !this.joinInFrame())
      window.open(this.offerUrlValue, "_blank", "noopener")
  }

  signIn(event: Event) {
    event.preventDefault()
    this.dispatch("open", { detail: { intent: "sign_in" } })
  }

  // data-action="email-code:verified->embed-account#verified"
  // The card turns back once the new answer is in, so the listener sees the
  // player they unlocked rather than the gate they left.
  verified() {
    this.#closeOnAnswer = true
    this.dispatch("refresh")
  }

  signOut() {
    void this.write("DELETE", this.sessionUrlValue)
  }

  private joinInFrame(): boolean {
    const state = this.#answer?.state
    if (state === "authentication_required") {
      this.dispatch("open", { detail: { intent: "join" } })
      return true
    }
    if (state === "join_required") {
      const body = this.#hostPage
        ? { membership: { host_page: this.#hostPage } }
        : undefined
      void this.write("POST", this.membershipUrlValue, body)
      return true
    }
    return false
  }

  // A sibling frame that signed in rotated the shared token, so a stale one
  // gets one fresh answer and one retry; otherwise Join or Sign out would
  // silently do nothing. Whatever happens, the next answer is the truth.
  private async write(method: string, url: string, body?: unknown) {
    if (this.#writing) return
    this.#writing = true
    try {
      const response = await this.send(method, url, body)
      if (response && (await tokenOutdated(response))) {
        const answer = new Promise<AccessAnswer | null>(
          (resolve) => (this.#waitingForAnswer = resolve),
        )
        this.dispatch("refresh")
        if (await answer) await this.send(method, url, body)
      }
    } finally {
      this.#writing = false
    }
    this.dispatch("refresh")
  }

  private async send(
    method: string,
    url: string,
    body?: unknown,
  ): Promise<Response | null> {
    const headers: Record<string, string> = {
      "X-CSRF-Token": this.#answer?.csrf_token ?? "",
    }
    if (body) headers["Content-Type"] = "application/json"
    try {
      return await frameFetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      })
    } catch {
      return null
    }
  }
}

async function tokenOutdated(response: Response): Promise<boolean> {
  if (response.status !== 422) return false
  try {
    const body = (await response.json()) as { state?: unknown }
    return body.state === "token_outdated"
  } catch {
    return false
  }
}
